import dynamic from 'next/dynamic';
import { FunctionComponent } from 'react';
import { ILandingPage, IPageSection } from '../../types/@types/generated/contentful';
import './Image';
import './RichText/RichText';

const HeroGraphicSection = dynamic(() => import('./HeroGraphicSection'));
const HeroStandardSection = dynamic(() => import('./HeroStandardSection'));
const HeroDonationSection = dynamic(() => import('./HeroDonationSection'));
const HeadlinerSection = dynamic(() => import('./HeadlinerSection'));
const RedeemPersonalMessage = dynamic(() => import('./RedeemPersonalMessage'));
const CausesSection = dynamic(() => import('./CausesSection'));
const PartnerLogosSection = dynamic(() => import('./PartnerLogosSection'));
const AppPromoSection = dynamic(() => import('./AppPromoSection'));
const ZigzagListSection = dynamic(() => import('./ZigzagListSection'));
const NewsletterSignupSection = dynamic(() => import('./NewsletterSignupSection'));
const ExternalEmbedSection = dynamic(() => import('./ExternalEmbedSection'));
const TeamtailorSection = dynamic(() => import('./TeamtailorSection'));
const ReviewsSection = dynamic(() => import('./ReviewsSection'));
const HorizontalListSection = dynamic(() => import('./HorizontalListSection'));
const ImpactersSection = dynamic(() => import('./ImpactersSection'));
const CampaignsSection = dynamic(() => import('./CampaignsSection'));
const HighlightSectionBleed = dynamic(() => import('./HighlightSectionBleed'));
const BigParagraphSection = dynamic(() => import('./BigParagraphSection'));
const HighlightSection = dynamic(() => import('./HighlightSection'));
const ContactUsCTASection = dynamic(() => import('./ContactUsCTASection'));
const DonationSection = dynamic(() => import('./DonationSection'));
const InstagramSection = dynamic(() => import('./InstagramSection'));
const QuotesSection = dynamic(() => import('./QuotesSection'));
const FormSection = dynamic(() => import('./FormSection'));
const SupportListSection = dynamic(() => import('./SupportListSection'));
const SmallListSection = dynamic(() => import('./SmallListSection'));
const ParagraphSection = dynamic(() => import('./ParagraphSection'));
const HeroFullscreenColorSection = dynamic(() => import('./HeroFullscreenColorSection'));
const VerticalListSection = dynamic(() => import('./VerticalListSection'));
const LastPushSection = dynamic(() => import('./LastPushSection'));
const ReceiptInfoSection = dynamic(() => import('./ReceiptInfoSection'));
const ShareableImageSection = dynamic(() => import('./ShareableImageSection'));
const IconSection = dynamic(() => import('./IconSection'));
const FAQSection = dynamic(() => import('./QuestionsAndAnswersSection'));
const CTAPush = dynamic(() => import('./CTAPush'));
const CenteredInfo = dynamic(() => import('./CenteredInfo'));

export type SectionProps = {
  page: ILandingPage;
  section: IPageSection;
  index?: number;
};

export type UnknownServerSideProps = Record<string, unknown> | null;

/**
 * has an issue with react-docgen-typescript => https://github.com/styleguidist/react-docgen-typescript/issues/342
 */
export type PageSectionComponent<T extends UnknownServerSideProps> = FunctionComponent<
  SectionProps & {
    serverSideProps: T;
  }
> & {
  getCustomServerSideProps?: (props: SectionProps) => Promise<T>;
};

export type PageSectionComponentClientOnly = FunctionComponent<SectionProps>;

export const SECTION_MAP_SERVER = {
  'hero-graphic': () => import('./HeroGraphicSection'),
  'hero-standard': () => import('./HeroStandardSection'),
  'hero-donation': () => import('./HeroDonationSection'),
  'partner-logos': () => import('./PartnerLogosSection'),
  'horizontal-list': () => import('./HorizontalListSection'),
  'zigzag-list': () => import('./ZigzagListSection'),
  headliner: () => import('./HeadlinerSection'),
  'redeem-personal-message': () => import('./RedeemPersonalMessage'),
  highlight: () => import('./HighlightSection'),
  'highlight-bleed': () => import('./HighlightSectionBleed'),
  'big-paragraph': () => import('./BigParagraphSection'),
  'small-list': () => import('./SmallListSection'),
  'contact-us-cta': () => import('./ContactUsCTASection'),
  'support-list': () => import('./SupportListSection'),

  quotes: () => import('./QuotesSection'),
  reviews: () => import('./ReviewsSection'),
  impacters: () => import('./ImpactersSection'),
  campaigns: () => import('./CampaignsSection'),
  donation: () => import('./DonationSection'),
  instagram: () => import('./InstagramSection'),
  'cause-pages': () => import('./CausesSection'),
  'newsletter-signup': () => import('./NewsletterSignupSection'),
  'external-embedded': () => import('./ExternalEmbedSection'),
  'teamtailor-jobs': () => import('./TeamtailorSection'),
  'app-promo': () => import('./AppPromoSection'),
  form: () => import('./FormSection'),
  paragraph: () => import('./ParagraphSection'),
  'hero-fullscreen-color': () => import('./HeroFullscreenColorSection'),
  'vertical-list': () => import('./VerticalListSection'),
  'last-push': () => import('./LastPushSection'),
  'receipt-info': () => import('./ReceiptInfoSection'),
  'shareable-image': () => import('./ShareableImageSection'),
  icon: () => import('./IconSection'),
  'questions-and-answers': () => import('./QuestionsAndAnswersSection'),
  'cta-push': () => import('./CTAPush'),
  'centered-info': () => import('./CenteredInfo'),
} as Record<string, () => Promise<{ default: PageSectionComponent<UnknownServerSideProps> }>>;

export const SECTION_MAP_RENDER = {
  'hero-graphic': HeroGraphicSection,
  'hero-standard': HeroStandardSection,
  'hero-donation': HeroDonationSection,
  'partner-logos': PartnerLogosSection,
  'horizontal-list': HorizontalListSection,
  'zigzag-list': ZigzagListSection,
  headliner: HeadlinerSection,
  'redeem-personal-message': RedeemPersonalMessage,
  highlight: HighlightSection,
  'highlight-bleed': HighlightSectionBleed,
  'big-paragraph': BigParagraphSection,
  'contact-us-cta': ContactUsCTASection,
  'support-list': SupportListSection,
  'small-list': SmallListSection,

  quotes: QuotesSection,
  impacters: ImpactersSection,
  campaigns: CampaignsSection,
  reviews: ReviewsSection,
  donation: DonationSection,
  instagram: InstagramSection,
  'cause-pages': CausesSection,
  'newsletter-signup': NewsletterSignupSection,
  'external-embedded': ExternalEmbedSection,
  'teamtailor-jobs': TeamtailorSection,
  'app-promo': AppPromoSection,
  form: FormSection,
  paragraph: ParagraphSection,
  'hero-fullscreen-color': HeroFullscreenColorSection,
  'vertical-list': VerticalListSection,
  'last-push': LastPushSection,
  'receipt-info': ReceiptInfoSection,
  'shareable-image': ShareableImageSection,
  icon: IconSection,
  'questions-and-answers': FAQSection,
  'cta-push': CTAPush,
  'centered-info': CenteredInfo,
} as Record<string, PageSectionComponent<UnknownServerSideProps>>;
